// Voz tempo-real (Fase 2). Fluxo desacoplado:
//   mic → Deepgram STT (WS direto, token efêmero) → no fim da fala (endpoint/UtteranceEnd)
//   → onUtterance(texto) [o chamador roda /ai/agent e devolve a resposta] → TTS (proxy /ai/voice/speak).
// interim_results alimentam o transcript ao vivo; barge-in corta o TTS quando o usuário volta a falar.

import { useCallback, useRef, useState } from 'react';

export type VoiceStatus = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';

interface Options {
  workspaceId: string | null;
  // Recebe a fala finalizada; retorna o texto a ser falado de volta (resposta do agente).
  onUtterance: (text: string) => Promise<string | void> | string | void;
}

export function useRealtimeVoice({ workspaceId, onUtterance }: Options) {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const keepAliveRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const finalsRef = useRef<string[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const stoppedRef = useRef(false); // true = encerrado de propósito (não reconectar)
  const retryRef = useRef(0);
  const startRef = useRef<() => void>(() => {});

  // Nível de áudio do mic (0..1) — alimenta o reator do HUD via rAF.
  const getLevel = useCallback(() => {
    const an = analyserRef.current;
    const data = dataRef.current;
    if (!an || !data) return 0;
    an.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i]! - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / data.length) * 3);
  }, []);

  const stopAudio = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.src = '';
      audioRef.current = null;
    }
  }, []);

  const speak = useCallback(
    async (text: string) => {
      if (!text || !workspaceId) {
        setStatus('listening');
        return;
      }
      try {
        setStatus('speaking');
        const res = await fetch('/api/ai/voice/speak', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ workspaceId, text }),
        });
        if (!res.ok) throw new Error('tts');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          if (audioRef.current === audio) audioRef.current = null;
          setStatus((s) => (s === 'speaking' ? 'listening' : s));
        };
        await audio.play().catch(() => {});
      } catch {
        setStatus('listening');
      }
    },
    [workspaceId],
  );

  const handleUtterance = useCallback(
    async (text: string) => {
      const clean = text.trim();
      if (!clean) return;
      setStatus('thinking');
      try {
        const reply = await onUtterance(clean);
        if (typeof reply === 'string' && reply.trim()) await speak(reply);
        else setStatus('listening');
      } catch {
        setStatus('listening');
      }
    },
    [onUtterance, speak],
  );

  const flushUtterance = useCallback(() => {
    if (!finalsRef.current.length) return;
    const utter = finalsRef.current.join(' ').trim();
    finalsRef.current = [];
    setTranscript('');
    void handleUtterance(utter);
  }, [handleUtterance]);

  const stop = useCallback(() => {
    stoppedRef.current = true; // intencional → não reconecta
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    try {
      if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    } catch {
      /* noop */
    }
    recRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* noop */
      }
      wsRef.current = null;
    }
    stopAudio();
    if (audioCtxRef.current) {
      try {
        void audioCtxRef.current.close();
      } catch {
        /* noop */
      }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    dataRef.current = null;
    finalsRef.current = [];
    setTranscript('');
    setStatus('idle');
  }, [stopAudio]);

  const start = useCallback(async () => {
    if (!workspaceId) {
      setError('Sem workspace ativo.');
      return;
    }
    setError(null);
    setStatus('connecting');
    stoppedRef.current = false;
    // Libera recursos de sessão anterior (caso reconnect).
    try {
      if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    } catch {
      /* noop */
    }
    recRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) {
      try {
        void audioCtxRef.current.close();
      } catch {
        /* noop */
      }
      audioCtxRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* noop */
      }
      wsRef.current = null;
    }
    try {
      // 1. token efêmero
      const tk = await fetch('/api/ai/voice/token', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspaceId }),
      });
      if (tk.status === 403) throw new Error('consent');
      if (!tk.ok) throw new Error('token');
      const { token, sttModel } = (await tk.json()) as { token: string; sttModel: string };

      // 2. microfone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      // Analyser p/ o reator (nível do mic). Sem connect ao destination → sem feedback.
      try {
        const Ctx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        dataRef.current = new Uint8Array(analyser.frequencyBinCount);
      } catch {
        /* analyser é opcional */
      }

      // 3. WS de STT (token via subprotocolo 'bearer')
      const params = new URLSearchParams({
        model: sttModel || 'nova-3',
        language: 'pt-BR',
        smart_format: 'true',
        punctuate: 'true',
        interim_results: 'true',
        endpointing: '300',
        utterance_end_ms: '1000',
        vad_events: 'true',
      });
      const ws = new WebSocket(`wss://api.deepgram.com/v1/listen?${params}`, ['bearer', token]);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0; // conexão ok → zera tentativas
        setStatus('listening');
        const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
        const rec = new MediaRecorder(stream, { mimeType: mime });
        recRef.current = rec;
        rec.ondataavailable = (e) => {
          if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data);
        };
        rec.start(250);
        keepAliveRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'KeepAlive' }));
        }, 8000);
      };

      ws.onmessage = (ev) => {
        let msg: {
          type?: string;
          is_final?: boolean;
          speech_final?: boolean;
          channel?: { alternatives?: { transcript?: string }[] };
        };
        try {
          msg = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        if (msg.type === 'UtteranceEnd') {
          flushUtterance();
          return;
        }
        if (msg.type !== 'Results') return;
        const text = msg.channel?.alternatives?.[0]?.transcript ?? '';
        // Barge-in: usuário voltou a falar enquanto o agente falava → corta o áudio.
        if (text && audioRef.current) stopAudio();
        if (msg.is_final) {
          if (text) finalsRef.current.push(text);
          setTranscript(finalsRef.current.join(' '));
          if (msg.speech_final) flushUtterance();
        } else if (text) {
          setTranscript([...finalsRef.current, text].join(' '));
        }
      };

      ws.onerror = () => setError('Conexão de voz falhou.');
      ws.onclose = () => {
        if (keepAliveRef.current) {
          clearInterval(keepAliveRef.current);
          keepAliveRef.current = null;
        }
        // Reconnect automático se a queda não foi intencional.
        if (!stoppedRef.current && retryRef.current < 5) {
          retryRef.current += 1;
          setStatus('connecting');
          window.setTimeout(() => {
            if (!stoppedRef.current) startRef.current();
          }, 1500);
        }
      };
    } catch (err) {
      const m = (err as Error).message;
      setError(
        m === 'consent'
          ? 'Consentimento de IA necessário (perfil → privacidade).'
          : m === 'token'
            ? 'Falha ao obter token de voz (verifique a key Deepgram no admin).'
            : 'Microfone/voz indisponível (verifique permissões).',
      );
      stop();
      setStatus('error');
    }
  }, [workspaceId, flushUtterance, stop, stopAudio]);
  startRef.current = start; // p/ reconnect chamar a versão atual

  return { status, transcript, error, start, stop, speak, getLevel };
}
